// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState } from 'react';
import './InvestmentAreasList.css';

const PRIORITY_COLORS = {
  HIGH: { color: 'var(--error)', bg: '#ffebee' },
  MEDIUM: { color: '#f57f17', bg: '#fff8e1' },
  LOW: { color: 'var(--success)', bg: '#e8f5e9' },
};

const CATEGORY_ICONS = {
  general: '🏛️',
  golf: '⛳',
  dining: '🍽️',
  amenities: '🎾',
  membership: '🪪',
  personnel: '👥',
};

export default function InvestmentAreasList({ areas, compact = false }) {
  const [expanded, setExpanded] = useState(null);

  function toggle(i) {
    setExpanded((prev) => (prev === i ? null : i));
  }

  // Determine max priority score for the bar widths
  const maxScore = Math.max(...areas.map((a) => a.priorityScore), 1);

  return (
    <div className="areas-list">
      {/* Header row */}
      {!compact && (
        <div className="areas-list__header" aria-hidden="true">
          <span className="areas-list__col-rank">#</span>
          <span className="areas-list__col-name">Investment Area</span>
          <span className="areas-list__col-score">Priority Score</span>
          <span className="areas-list__col-mentions">Mentions</span>
          <span className="areas-list__col-priorities">Breakdown</span>
        </div>
      )}

      <ol className="areas-list__items" aria-label="Investment areas ranked by priority">
        {areas.map((area, i) => {
          const dominantPriority = area.highCount > 0 ? 'HIGH' : area.mediumCount > 0 ? 'MEDIUM' : 'LOW';
          const priorityStyle = PRIORITY_COLORS[dominantPriority];
          const isOpen = expanded === i;

          return (
            <li key={i} className={`areas-list__item${isOpen ? ' areas-list__item--open' : ''}`}>
              <button
                className="areas-list__row"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                aria-controls={`area-detail-${i}`}
              >
                {!compact && (
                  <span className="areas-list__rank">{i + 1}</span>
                )}
                <span className="areas-list__name-wrap">
                  <span className="areas-list__name">{area.area}</span>
                  {area.categories.length > 0 && (
                    <span className="areas-list__cats" aria-label="Related categories">
                      {area.categories.map((c) => (
                        <span key={c} title={c} aria-label={c}>
                          {CATEGORY_ICONS[c] || c}
                        </span>
                      ))}
                    </span>
                  )}
                </span>

                {!compact && (
                  <span className="areas-list__score-bar-wrap" aria-label={`Priority score: ${area.priorityScore}`}>
                    <span
                      className="areas-list__score-bar"
                      style={{
                        width: `${(area.priorityScore / maxScore) * 100}%`,
                        background: priorityStyle.color,
                      }}
                    />
                    <span className="areas-list__score-value">{area.priorityScore}</span>
                  </span>
                )}

                <span className="areas-list__mentions" aria-label={`${area.mentions} mention${area.mentions !== 1 ? 's' : ''}`}>
                  {area.mentions}×
                </span>

                <span className="areas-list__priority-chips" aria-label="Priority breakdown">
                  {area.highCount > 0 && (
                    <span className="areas-list__chip areas-list__chip--high">
                      {area.highCount} H
                    </span>
                  )}
                  {area.mediumCount > 0 && (
                    <span className="areas-list__chip areas-list__chip--medium">
                      {area.mediumCount} M
                    </span>
                  )}
                  {area.lowCount > 0 && (
                    <span className="areas-list__chip areas-list__chip--low">
                      {area.lowCount} L
                    </span>
                  )}
                </span>

                <span className="areas-list__chevron" aria-hidden="true">
                  {isOpen ? '▲' : '▼'}
                </span>
              </button>

              {/* Expandable detail */}
              <div
                id={`area-detail-${i}`}
                className="areas-list__detail"
                hidden={!isOpen}
                aria-label={`Details for ${area.area}`}
              >
                {area.sampleDescriptions.length > 0 && (
                  <div className="areas-list__descriptions">
                    <p className="areas-list__desc-label">Member feedback examples:</p>
                    <ul>
                      {area.sampleDescriptions.map((d, di) => (
                        <li key={di}>"{d}"</li>
                      ))}
                    </ul>
                  </div>
                )}
                {area.categories.length > 0 && (
                  <p className="areas-list__cats-detail">
                    <strong>Categories:</strong>{' '}
                    {area.categories.map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join(', ')}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
